/**
 * «Течение искр»: детерминированный план показа слова FRAMERIVER на превью во время генерации.
 * Буквы идут по порядку слова группами по 1–3, каждая группа держится своё время,
 * плывёт по течению и встаёт туда, где не перекрывает уходящую.
 * Чистые функции без DOM — план считается и в воркере, и на главном потоке.
 */

export const MOTION_WORD = "FRAMERIVER"

/** Видимая ширина буквы в долях кегля (fallback, пока не измерен шрифт). */
export const DEFAULT_GLYPH_WIDTHS: Record<string, number> = {
  F: 0.56,
  R: 0.62,
  A: 0.68,
  M: 0.82,
  E: 0.56,
  I: 0.27,
  V: 0.66,
}

/** Половина высоты прописной буквы в долях кегля. */
export const DEFAULT_CAP_HALF = 0.36

/** Скорость течения: доля ширины превью в секунду. */
export const MOTION_FLOW = 0.05

/** Меньше этой стороны буквы не показываем — остаются только точки и течение. */
export const MOTION_MIN_LETTER_SIDE = 72

export const SPARK_IN_STAGGER_MS = 220
export const SPARK_IN_SPREAD_MS = 420
export const SPARK_IN_MS = 600
export const SPARK_OUT_STAGGER_MS = 110
export const SPARK_OUT_MS = 750

export function motionRandom(i: number) {
  const x = Math.sin(i * 12.9898 + 78.233) * 43758.5453
  return x - Math.floor(x)
}

export function motionSeed(key: string) {
  let h = 7
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) % 100003
  return h
}

/** Шаг точечной сетки и радиус точки (CSS px) — как в прототипе. */
export function motionDotGrid(width: number, height: number) {
  const step = Math.max(5, Math.min(8, Math.round(Math.min(width, height) / 20)))
  const radius = Math.max(0.7, Math.round(step * 0.14 * 10) / 10)
  return { step, radius }
}

export type MotionLetter = {
  ch: string
  /** Центр видимой части буквы, CSS px. */
  cx: number
  cy: number
  fontSize: number
  /** Период покачивания, мс. */
  bobPeriod: number
  bobPhase: number
}

export type MotionGroup = {
  index: number
  pos: number
  k: number
  /** Сколько группа держится до появления следующей, мс. */
  duration: number
  /** Смещение по течению за всю жизнь группы, CSS px. */
  drift: number
  ox: number
  oy: number
  width: number
  height: number
  letters: MotionLetter[]
}

export type MotionPlanner = {
  step: number
  bob: number
  lettersEnabled: boolean
  group: (index: number) => MotionGroup
}

type Layout = { pts: { ch: string; x: number; y: number }[]; minX: number; maxX: number; minY: number; maxY: number }

function relLayout(
  chars: string[],
  kind: "row" | "stair",
  fs: number,
  seed: number,
  widthOf: (ch: string) => number,
  capHalf: number,
): Layout {
  const pts: Layout["pts"] = []
  const slope = (motionRandom(seed + 1.3) - 0.5) * 0.3 * fs
  let x = 0
  let y = 0
  chars.forEach((ch, j) => {
    if (j > 0) {
      if (kind === "row") {
        x += ((widthOf(chars[j - 1]) + widthOf(ch)) / 2 + 0.14) * fs + (motionRandom(seed + j * 2.1) - 0.5) * 0.08 * fs
        y += slope + (motionRandom(seed + j * 4.3) - 0.5) * 0.24 * fs
      } else {
        x += (0.3 + motionRandom(seed + j * 2.1) * 0.14) * fs
        y += (capHalf * 2 + 0.08) * fs
      }
    }
    pts.push({ ch, x, y })
  })
  let minX = Infinity
  let maxX = -Infinity
  let minY = Infinity
  let maxY = -Infinity
  for (const p of pts) {
    const hw = (widthOf(p.ch) * fs) / 2
    minX = Math.min(minX, p.x - hw)
    maxX = Math.max(maxX, p.x + hw)
    minY = Math.min(minY, p.y - capHalf * fs)
    maxY = Math.max(maxY, p.y + capHalf * fs)
  }
  return { pts, minX, maxX, minY, maxY }
}

function overlapArea(a: number[], b: number[]) {
  const w = Math.min(a[2], b[2]) - Math.max(a[0], b[0])
  const h = Math.min(a[3], b[3]) - Math.max(a[1], b[1])
  return w > 0 && h > 0 ? w * h : 0
}

export type MotionPlannerOptions = {
  width: number
  height: number
  seed: number
  flow?: number
  glyphWidths?: Record<string, number>
  capHalf?: number
  /** Нижняя полоса (CSS px) под подпись статуса — буквы в неё не заходят. */
  reserveBottom?: number
}

export function createMotionPlanner({
  width: w,
  height: fullHeight,
  seed,
  flow = MOTION_FLOW,
  glyphWidths = DEFAULT_GLYPH_WIDTHS,
  capHalf = DEFAULT_CAP_HALF,
  reserveBottom = 0,
}: MotionPlannerOptions): MotionPlanner {
  const { step } = motionDotGrid(w, fullHeight)
  const h = fullHeight - Math.max(0, Math.min(reserveBottom, fullHeight * 0.4))
  const m = step
  const bob = step * 0.45
  const widthOf = (ch: string) => glyphWidths[ch] ?? DEFAULT_GLYPH_WIDTHS[ch] ?? 0.6
  const capScale = DEFAULT_CAP_HALF / capHalf
  const fsBase = Math.max(20, Math.floor(Math.min((h - 2 * m) * 0.87 * capScale, ((w - 2 * m) * 0.5) / widthOf("M"))))
  const lettersEnabled = Math.min(w, h) >= MOTION_MIN_LETTER_SIDE
  const groups: MotionGroup[] = []

  function build(g: number): MotionGroup {
    const prev = g > 0 ? groups[g - 1] : null
    const pos = prev ? (prev.pos + prev.k) % MOTION_WORD.length : 0
    const sd = seed + g * 101
    const r = motionRandom(sd + 0.5)
    const kWant = Math.min(r < 0.42 ? 1 : r < 0.78 ? 2 : 3, MOTION_WORD.length - pos)
    const v = ((w * flow) / 1000) * (0.8 + motionRandom(sd + 3.7) * 0.4)
    const sz = 0.86 + motionRandom(sd + 6.1) * 0.14
    let plan: { k: number; duration: number; drift: number; lay: Layout; s: number } | null = null
    for (let k = kWant; k >= 1; k--) {
      const chars = MOTION_WORD.slice(pos, pos + k).split("")
      const hold = 700 + motionRandom(sd + 9.3) * 1300 + 300 * (k - 1) + (pos + k === MOTION_WORD.length ? 500 : 0)
      const duration = Math.round(1000 + SPARK_IN_STAGGER_MS * (k - 1) + hold)
      const drift = Math.round(v * (duration + 2400))
      const availW = w - 2 * m - drift
      const availH = h - 2 * m - 2 * bob
      const fit = (lay: Layout) => Math.min(sz, availW / (lay.maxX - lay.minX), availH / (lay.maxY - lay.minY))
      const row = relLayout(chars, "row", fsBase, sd, widthOf, capHalf)
      const stair = relLayout(chars, "stair", fsBase, sd, widthOf, capHalf)
      const sr = fit(row)
      const ss = fit(stair)
      const useStair = k > 1 && (ss > sr * 1.1 || (ss > sr / 1.1 && motionRandom(sd + 12.9) < 0.4))
      const s = useStair ? ss : sr
      if (s >= 0.6 || k === 1) {
        plan = { k, duration, drift, lay: useStair ? stair : row, s: Math.max(0.3, s) }
        break
      }
    }
    if (!plan) throw new Error("generation motion: empty group plan")
    const { lay, s, drift, duration, k } = plan
    const gw = (lay.maxX - lay.minX) * s
    const gh = (lay.maxY - lay.minY) * s + 2 * bob
    const pad = fsBase * 0.1
    const prevRect = prev
      ? [
          prev.ox + prev.drift * (prev.duration / (prev.duration + duration)) - pad,
          prev.oy - pad,
          prev.ox + prev.width + prev.drift + pad,
          prev.oy + prev.height + pad,
        ]
      : null
    let best = { ox: m, oy: m, score: Infinity }
    for (let c = 0; c < 18; c++) {
      const ox = m + motionRandom(sd + 20 + c * 1.1) * Math.max(0, w - 2 * m - drift - gw)
      const oy = m + motionRandom(sd + 40 + c * 1.3) * Math.max(0, h - 2 * m - gh)
      const score = prevRect ? overlapArea([ox, oy, ox + gw + drift * 0.5, oy + gh], prevRect) : 0
      if (score < best.score) best = { ox, oy, score }
      if (score === 0) break
    }
    const letters = lay.pts.map((p, j) => ({
      ch: p.ch,
      cx: best.ox + (p.x - lay.minX) * s,
      cy: best.oy + bob + (p.y - lay.minY) * s,
      fontSize: fsBase * s,
      bobPeriod: 2200 + motionRandom(sd + j * 5.9) * 900,
      bobPhase: motionRandom(sd + j * 7.7) * 3000,
    }))
    return { index: g, pos, k, duration, drift, ox: best.ox, oy: best.oy, width: gw, height: gh, letters }
  }

  return {
    step,
    bob,
    lettersEnabled,
    group(index: number) {
      while (groups.length <= index) groups.push(build(groups.length))
      return groups[index]
    },
  }
}
